import axiosInstance from '@/services/axiosInstance'
import { UserV2 as User } from '@/types/UserV2'
import { UserFilters } from '@/types/filter/UserFilters'
import { PaginationParams } from '@/types/filter/PaginationParams'
import { SortParams } from '@/types/filter/SortParams'
import { toBackendDate } from '@/util/datetimeutil'

// // Lấy danh sách Follow với keyword, page và size
/**
 * {
 *     "code": "00",
 *     "message": "Thành công",
 *     "data": {
 *         "items": [
 *             {}, {}, {}, {}
 *         ],
 *         "page": 1,
 *         "size": 20,
 *         "total_pages": 1,
 *         "total_items": 2
 *     },
 *     "is_success": true
 * }
 */
export const apiGetUsers = async (filters: UserFilters, pagination: PaginationParams, sort?: SortParams) => {
  // Xử lý các tham số `sort` và `pagination`
  const { page, size } = pagination
  const { sort_field, sort_direction } = sort || {}

  const sortParam = sort_field && sort_direction ? `${sort_field},${sort_direction}` : undefined

  const response = await axiosInstance.get(`/api/v1/users`, {
    params: {
      ...filters,
      page,
      size,
      ...(sortParam ? { sort: sortParam } : {}),
    },
  })

  return response.data // Response trả về theo cấu trúc: { ok, timestamp, data: { items, total_item, total_page, ... } }
}

export const apiGetUser = async (id: number) => {
  const response = await axiosInstance.get(`/api/v1/users/` + id)
  return response.data
}

// Tạo User mới
export const apiCreateUser = async (payload: User) => {
  const response = await axiosInstance.post(`/user-svc/api/v1/users`, payload)
  return response.data
}

// Tạo User mới (API v2 - multipart/form-data)
export const apiCreateUserV2 = async (user: User, file?: File) => {
  const formData = buildFormData(user, file)
  const response = await axiosInstance.post(`/api/v2/users`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const apiUpdateUserV2 = async (user: User, file?: File) => {
  const formData = buildFormData(user, file)
  const response = await axiosInstance.patch(`/api/v2/users/${user.id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

const buildFormData = (user: User, file?: File): FormData => {
  const payload = {
    ...user,
    date_of_birth: toBackendDate(user.date_of_birth),
    start_date: toBackendDate(user.start_date),
    terminal_date: toBackendDate(user.terminal_date),
  }
  const formData = new FormData()

  formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }))

  if (file) {
    formData.append('file', file)
  }

  return formData
}

export const apiDeleteUser = async (id: any) => {
  const response = await axiosInstance.delete(`/api/v1/users/${id}`)
  return response.data
}

export const apiBulkDeleteUsers = async (ids: number[]) => {
  const response = await axiosInstance.delete(`/api/v1/users/batch`, {
    data: ids, // Truyền trực tiếp mảng ids vào body
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiResetPassword = async (id: any, password: any) => {
  const response = await axiosInstance.post(`/api/v1/users/${id}/reset-password`, {
    password,
  })
  return response.data
}

export const apiGetPicList = async () => {
  const response = await axiosInstance.get(`/api/v1/users/pic-list`)
  return response.data
}

export const apiChangePassword = async (current_password: string, new_password: string) => {
  const params = {
    current_password,
    new_password,
  }
  const response = await axiosInstance.put(`/api/v1/me/change-password`, params)

  return response.data // Response trả về theo cấu trúc: { ok, timestamp, data: { items, total_item, total_page, ... } }
}
